import { useDraggable } from '@dnd-kit/core';
import { Badge, cn, IconButton, Tooltip, TooltipContent, TooltipTrigger } from '@aibi/ui';
import { Check, GripVertical, Plus, X } from 'lucide-react';
import type { FieldDescriptor } from '../../model/field-catalog';
import type { DragSource } from '../../model/drop-rules';
import { DATA_TYPE_LABELS, ROLE_LABELS } from './field-labels';
import { FieldIcon } from './FieldIcon';

export interface FieldItemProps {
  readonly field: FieldDescriptor;
  /** El campo ya forma parte de la visualizacion seleccionada. */
  readonly inUse: boolean;
  readonly onAdd: (field: FieldDescriptor) => void;
  readonly onRemove: () => void;
}

/**
 * Campo arrastrable del explorador. Se arrastra con puntero o teclado (Espacio/Enter);
 * el boton "+" lo agrega a la visualizacion seleccionada y, si ya esta en uso, la marca
 * se convierte en "x" al pasar el puntero para quitarlo.
 */
export function FieldItem({ field, inUse, onAdd, onRemove }: FieldItemProps) {
  const source: DragSource = { type: 'field', fieldId: field.id };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `field:${field.id}`,
    data: { source },
  });

  return (
    <li
      className={cn(
        'group flex h-row-compact items-center gap-1 rounded-md pr-1 text-sm hover:bg-accent',
        isDragging && 'opacity-40',
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            aria-roledescription="campo arrastrable"
            aria-label={`${field.name} (${ROLE_LABELS[field.role]})`}
            className="flex min-w-0 flex-1 cursor-grab items-center gap-1.5 rounded-md py-1 pl-1 outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
            onDoubleClick={() => {
              onAdd(field);
            }}
          >
            <GripVertical
              aria-hidden
              className="size-3 shrink-0 text-muted-foreground/0 group-hover:text-muted-foreground"
            />
            <FieldIcon role={field.role} />
            <span className={cn('truncate', inUse && 'font-medium')}>{field.name}</span>
            {field.calculated && (
              <Badge variant="outline" className="ml-auto">
                fx
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="space-y-1">
          <p className="font-semibold">{field.name}</p>
          <p>
            {ROLE_LABELS[field.role]} · {DATA_TYPE_LABELS[field.dataType] ?? field.dataType}
            {field.aggregation !== null && ` · ${field.aggregation}`}
            {field.folder !== null && ` · ${field.folder}`}
          </p>
          {field.description !== null && <p className="opacity-80">{field.description}</p>}
        </TooltipContent>
      </Tooltip>
      {inUse ? (
        <IconButton
          label={`Quitar ${field.name} de la visualizacion`}
          className="group/remove size-6 text-primary hover:text-destructive focus-visible:text-destructive"
          onClick={onRemove}
        >
          <Check className="group-hover/remove:hidden group-focus-visible/remove:hidden" />
          <X className="hidden group-hover/remove:block group-focus-visible/remove:block" />
        </IconButton>
      ) : (
        <IconButton
          label={`Agregar ${field.name}`}
          className="size-6 opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
          onClick={() => {
            onAdd(field);
          }}
        >
          <Plus />
        </IconButton>
      )}
    </li>
  );
}
